import * as THREE from 'three';
import type { Heightfield } from './TerrainTypes';
import { sampleHeightWorld } from './TerrainTypes';

export interface RaycastHit {
  point: THREE.Vector3;
  /** Parametric distance along the ray. */
  distance: number;
}

/**
 * Analytically march a ray against a heightfield and return the first surface crossing, refined by
 * bisection. Pure and unit-testable (spec §2.5 "ray→height sampling math"). Works in terrain-local
 * space (apply the terrain's inverse rotation before calling if needed).
 */
export function raycastHeightfield(
  field: Heightfield,
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  options: { maxDistance?: number; step?: number } = {},
): RaycastHit | null {
  const dir = direction.clone().normalize();
  const maxDistance = options.maxDistance ?? 2000;
  const step = options.step ?? Math.min(field.widthMeters, field.lengthMeters) / field.cols;

  // signed distance = ray.y - terrainHeight; a sign change means we crossed the surface.
  const p = new THREE.Vector3();
  let prevT = 0;
  p.copy(origin);
  let prevDiff = p.y - sampleHeightWorld(field, p.x, p.z);

  for (let t = step; t <= maxDistance; t += step) {
    p.copy(origin).addScaledVector(dir, t);
    const diff = p.y - sampleHeightWorld(field, p.x, p.z);
    if (diff <= 0 && prevDiff > 0) {
      // Crossing between prevT and t; bisect to refine.
      let lo = prevT;
      let hi = t;
      for (let i = 0; i < 24; i += 1) {
        const mid = (lo + hi) * 0.5;
        p.copy(origin).addScaledVector(dir, mid);
        const d = p.y - sampleHeightWorld(field, p.x, p.z);
        if (d <= 0) {
          hi = mid;
        } else {
          lo = mid;
        }
      }
      const hitT = (lo + hi) * 0.5;
      const point = origin.clone().addScaledVector(dir, hitT);
      point.y = sampleHeightWorld(field, point.x, point.z);
      return { point, distance: hitT };
    }
    prevDiff = diff;
    prevT = t;
  }
  return null;
}

/**
 * Screen/mesh raycaster wrapper. Converts normalized device coordinates + camera into a world-space
 * point on the terrain mesh using Three.js's raycaster (used at runtime; the pure
 * {@link raycastHeightfield} covers the math in tests).
 */
export class TerrainRaycaster {
  private readonly raycaster = new THREE.Raycaster();

  /** @param ndc normalized device coords in [-1, 1] for both axes. */
  intersect(ndc: THREE.Vector2, camera: THREE.Camera, terrainMesh: THREE.Object3D): THREE.Vector3 | null {
    this.raycaster.setFromCamera(ndc, camera);
    const hits = this.raycaster.intersectObject(terrainMesh, false);
    return hits.length > 0 && hits[0] ? hits[0].point.clone() : null;
  }
}
